import { useState } from 'react';
import { useResultData } from '../../context';
import { tokens } from '../../tokens';

interface Props {
  cellSize?: number;
  gap?: number;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const cellColor = (pct: number): string => {
  const value = pct * 100;
  if (value > 20) return tokens.green;
  if (value > 5) return 'rgba(0,176,97,0.65)';
  // Small moves still get a clear red/green — a deep shade, never a flat gray.
  if (value >= 0) return 'rgba(0,176,97,0.4)';
  if (value > -5) return 'rgba(240,22,47,0.4)';
  if (value > -20) return 'rgba(240,22,47,0.5)';
  return tokens.red;
};

interface HoverState {
  left: number;
  top: number;
  label: string;
  pct: number | null;
}

const CalendarHeatmap = ({ cellSize = 28, gap = 3 }: Props) => {
  const { heatmap } = useResultData();
  const [hover, setHover] = useState<HoverState | null>(null);
  const years = Object.keys(heatmap)
    .map((year) => parseInt(year, 10))
    .sort((a, b) => a - b);

  return (
    <div style={{ display: 'inline-block', position: 'relative' }}>
      <div style={{ display: 'flex', gap, marginBottom: 4 }}>
        {/* Empty spacer with the exact box of the year label, so the flex
         * layout lines each month up with its cell column — independent of
         * box-sizing and the gap width. */}
        <div style={{ width: 24, paddingRight: 4 }} />
        {MONTHS.map((month) => (
          <div
            key={month}
            style={{
              width: cellSize,
              fontSize: 10,
              color: tokens.textMuted,
              textAlign: 'center',
            }}
          >
            {month}
          </div>
        ))}
      </div>
      {years.map((year) => (
        <div
          key={year}
          style={{
            display: 'flex',
            gap,
            marginBottom: gap,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: 24,
              fontSize: 11,
              color: tokens.textMuted,
              textAlign: 'right',
              paddingRight: 4,
            }}
          >
            {year}
          </div>
          {MONTHS.map((monthName, monthIndex) => {
            const cell = heatmap[year]?.[monthIndex];
            const pct = cell?.pct ?? null;
            return (
              <div
                key={`${year}-${monthName}`}
                className="nc-heatmap-cell"
                onMouseEnter={(event) => {
                  const element = event.currentTarget;
                  setHover({
                    left: element.offsetLeft + element.offsetWidth / 2,
                    top: element.offsetTop + element.offsetHeight,
                    label: `${monthName} ${year}`,
                    pct,
                  });
                }}
                onMouseLeave={() => setHover(null)}
                style={{
                  width: cellSize,
                  height: cellSize,
                  borderRadius: 3,
                  background:
                    pct === null ? 'rgba(255,255,255,0.02)' : cellColor(pct),
                  cursor: 'default',
                }}
              />
            );
          })}
        </div>
      ))}
      {hover && (
        <div
          style={{
            position: 'absolute',
            left: hover.left,
            top: hover.top + 6,
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.92)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 4,
            padding: '4px 8px',
            fontSize: 11,
            fontFamily: tokens.fontMono,
            color: tokens.textSecondary,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          {hover.label}
          {' · '}
          <span
            style={{
              fontWeight: 600,
              color:
                hover.pct === null
                  ? tokens.textMuted
                  : hover.pct >= 0
                  ? tokens.green
                  : tokens.red,
            }}
          >
            {hover.pct === null
              ? '—'
              : `${hover.pct >= 0 ? '+' : ''}${(hover.pct * 100).toFixed(1)}%`}
          </span>
        </div>
      )}
    </div>
  );
};

export default CalendarHeatmap;
